// ════════════════════════════════════════════════════════════
// PropuestaFacturaIntervencion — revisar y facturar una intervención
// Muestra las líneas del tarifario y los recambios, con el coste y el margen.
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Grid, Typography,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TextField, IconButton,
  Select, MenuItem, FormControl, InputLabel, Alert, Chip, CircularProgress, InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getPropuestaFactura, facturarIntervencion, getConfigERP,
  crearProforma, descargarProformaPDF, confirmarValoracion } from '../api';

const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
const r2 = (n: number) => Math.round(n * 100) / 100;

export default function PropuestaFacturaIntervencion({ open, intervencionId, onClose, onFacturada, modo = 'factura' }: any) {
  const [datos, setDatos] = useState<any>(null);
  const [lineas, setLineas] = useState<any[]>([]);
  const [formas, setFormas] = useState<any[]>([]);
  const [formaPago, setFormaPago] = useState('contado');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [cargando, setCargando] = useState(true);
  const [emitiendo, setEmitiendo] = useState(false);

  useEffect(() => {
    if (!open || !intervencionId) return;
    setCargando(true);
    setDatos(null);
    getConfigERP().then((c: any) => setFormas(c.formas_pago || [])).catch(() => {});
    getPropuestaFactura(intervencionId)
      .then((d: any) => {
        setDatos(d);
        setLineas((d.lineas || []).map((l: any) => ({
          concepto: l.concepto, cantidad: l.cantidad, precio_unitario: l.precio_unitario,
          descuento: 0, tipo_iva: 21, coste: l.coste || 0, tipo: l.tipo,
        })));
      })
      .catch((e: any) => alert(e.response?.data?.detail || 'No se pudo cargar la propuesta'))
      .finally(() => setCargando(false));
  }, [open, intervencionId]);

  const setLinea = (i: number, campo: string, valor: any) => {
    const nuevas = [...lineas];
    nuevas[i] = { ...nuevas[i], [campo]: valor };
    setLineas(nuevas);
  };

  let base = 0;
  let cuota = 0;
  lineas.forEach(l => {
    const b = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0)
      * (1 - (parseFloat(l.descuento) || 0) / 100);
    base += b;
    cuota += b * (parseFloat(l.tipo_iva) || 0) / 100;
  });
  base = r2(base);
  cuota = r2(cuota);

  const costeTotal = datos?.resumen?.coste_total || 0;
  const margen = r2(base - costeTotal);
  const margenPct = base > 0 ? r2(margen / base * 100) : null;

  const generarProforma = async () => {
    const validas = lineas.filter(l => (l.concepto || '').trim() && (parseFloat(l.cantidad) || 0) > 0);
    if (!validas.length) { alert('No hay líneas para la proforma'); return; }
    setEmitiendo(true);
    try {
      const r = await crearProforma({
        cliente_id: datos.intervencion.cliente_id,
        maquina_id: datos.intervencion.maquina_id,
        fecha, forma_pago: formaPago, validez_dias: 30,
        origen_tipo: 'intervencion', origen_id: intervencionId,
        observaciones: `Intervención INT-${intervencionId}`,
        lineas: validas.map(l => ({
          concepto: l.concepto.trim(), cantidad: parseFloat(l.cantidad) || 0,
          precio_unitario: parseFloat(l.precio_unitario) || 0,
          descuento: parseFloat(l.descuento) || 0, tipo_iva: parseFloat(l.tipo_iva) || 0,
        })),
      });
      if (window.confirm(`✅ Proforma ${r.numero} creada.\n\n¿Descargar el PDF para enviárselo al cliente?`)) {
        await descargarProformaPDF(r.id, r.numero);
      }
      onClose();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al crear la proforma');
    } finally {
      setEmitiendo(false);
    }
  };

  const confirmar = async () => {
    const validas = lineas.filter(l => (l.concepto || '').trim());
    if (!validas.length) { alert('No hay líneas que valorar'); return; }
    setEmitiendo(true);
    try {
      const r = await confirmarValoracion(intervencionId, validas);
      const destinos: any = {
        administracion: 'Pasa a Administración, pendiente de facturar.',
        garantias: 'Pasa a Gestión de garantías para repartir los costes.',
        ninguno: 'Valoración guardada. El coste queda imputado a la máquina.',
      };
      alert(`✅ Valoración confirmada.\n\n${destinos[r.destino] || ''}`);
      onFacturada?.();
      onClose();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'No se pudo confirmar la valoración');
    } finally { setEmitiendo(false); }
  };

  const emitir = async () => {
    const validas = lineas.filter(l => (l.concepto || '').trim() && (parseFloat(l.cantidad) || 0) > 0);
    if (!validas.length) { alert('No hay líneas que facturar'); return; }
    if (!window.confirm(
      `Se emitirá la factura por ${eur(base + cuota)} a ${datos.intervencion.cliente_nombre}.\n\n` +
      `Podrás seguir editándola en Facturas mientras no se envíe a Hacienda. ¿Continuar?`)) return;
    setEmitiendo(true);
    try {
      const r = await facturarIntervencion(intervencionId, {
        fecha, forma_pago: formaPago,
        lineas: validas.map(l => ({
          concepto: l.concepto.trim(), cantidad: parseFloat(l.cantidad) || 0,
          precio_unitario: parseFloat(l.precio_unitario) || 0,
          descuento: parseFloat(l.descuento) || 0, tipo_iva: parseFloat(l.tipo_iva) || 0,
        })),
      });
      alert(`✅ Factura ${r.numero} emitida.`);
      if (onFacturada) onFacturada(r);
      onClose();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al emitir la factura');
    } finally {
      setEmitiendo(false);
    }
  };

  const i = datos?.intervencion;
  const res = datos?.resumen || {};

  return (
    <Dialog open={!!open} maxWidth="lg" fullWidth
      onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') onClose(); }}>
      <DialogTitle>🧾 Facturar intervención INT-{intervencionId}</DialogTitle>
      <DialogContent>
        {cargando ? <CircularProgress /> : !datos ? (
          <Alert severity="error">No se pudo cargar la propuesta.</Alert>
        ) : (
          <Box>
            <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }} alignItems="center">
              <Grid item xs={12} md={5}>
                <Typography variant="body2"><b>{i.cliente_nombre}</b></Typography>
                <Typography variant="caption" color="text.secondary">
                  {i.modelo ? `${i.modelo} · ${i.numero_serie || ''}` : 'Sin máquina asociada'}
                  {i.tipo_intervencion ? ` · ${i.tipo_intervencion}` : ''}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" type="date" label="Fecha de factura"
                  InputLabelProps={{ shrink: true }} value={fecha}
                  onChange={(e) => setFecha(e.target.value)} />
              </Grid>
              <Grid item xs={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Forma de pago</InputLabel>
                  <Select value={formas.some((f: any) => f.clave === formaPago) ? formaPago : 'contado'}
                    label="Forma de pago" onChange={(e) => setFormaPago(String(e.target.value))}>
                    {formas.map((f: any) => <MenuItem key={f.clave} value={f.clave}>{f.nombre}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 840 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 90 }}>Cant.</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 110 }}>Precio</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 80 }}>Dto %</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 90 }}>IVA</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineas.map((l, idx) => {
                    const importe = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0)
                      * (1 - (parseFloat(l.descuento) || 0) / 100);
                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField fullWidth variant="standard" value={l.concepto}
                            onChange={(e) => setLinea(idx, 'concepto', e.target.value)} />
                          <Chip size="small" variant="outlined" sx={{ mt: 0.3, fontSize: 10, height: 18 }}
                            label={l.tipo === 'material' ? 'Recambio' : 'Mano de obra / desplazamiento'} />
                        </TableCell>
                        <TableCell>
                          <TextField variant="standard" type="number" value={l.cantidad}
                            onChange={(e) => setLinea(idx, 'cantidad', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField variant="standard" type="number" value={l.precio_unitario}
                            onChange={(e) => setLinea(idx, 'precio_unitario', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField variant="standard" type="number" value={l.descuento}
                            onChange={(e) => setLinea(idx, 'descuento', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <Select variant="standard" fullWidth value={Number(l.tipo_iva)}
                            onChange={(e) => setLinea(idx, 'tipo_iva', e.target.value)}>
                            {[21, 10, 4, 0].map(p => <MenuItem key={p} value={p}>{p}%</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{eur(importe)}</TableCell>
                        <TableCell>
                          <IconButton size="small" color="error"
                            onClick={() => setLineas(lineas.filter((_, j) => j !== idx))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }}
              onClick={() => setLineas([...lineas, {
                concepto: '', cantidad: 1, precio_unitario: 0, descuento: 0,
                tipo_iva: 21, coste: 0, tipo: 'libre' }])}>
              Añadir línea
            </Button>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6} sx={{ ml: 'auto' }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>A FACTURAR</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2">Base imponible</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(base)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">IVA</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(cuota)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5,
                    pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontWeight: 700 }}>TOTAL</Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{eur(base + cuota)}</Typography>
                  </Box>
                </Box>
                {base === 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Importe cero: si es una garantía, el coste quedará igualmente imputado a la máquina.
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        {modo === 'valoracion' ? (
          <Button variant="contained" onClick={confirmar} disabled={emitiendo || cargando}
            sx={{ bgcolor: '#22c55e' }}>
            {emitiendo ? 'Confirmando...' : '✅ Confirmar valoración'}
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={generarProforma} disabled={emitiendo || cargando}>
              📄 Generar proforma
            </Button>
            <Button variant="contained" onClick={emitir} disabled={emitiendo || cargando}
              sx={{ bgcolor: '#4f46e5' }}>
              {emitiendo ? 'Emitiendo...' : 'Emitir factura'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
